/**
 * RotationMacrosPanel — modernized rotation-macro section of the AbilityTestRig.
 *
 * Record / save / cancel a cast sequence, stop the active player, and list each
 * saved rotation with play/pause/resume/delete + live step progress chips.
 */
import React from 'react';
import {
  COLORS,
  SPACE,
  FONT_SIZE,
} from '../../theme';
import { Section } from './shared';
import { rigStyles as styles } from './abilityRigStyles';
import type {
  RotationDefinition,
  RotationStatus,
} from '../../hooks/useRotationPlayer';
import { Button, Badge } from '../../primitives';

const RotationRow: React.FC<{
  rotation: RotationDefinition;
  isActive: boolean;
  playerStatus: RotationStatus;
  currentStep: number;
  onPlay: () => void;
  onPause: () => void;
  onResume: () => void;
  onDelete: () => void;
}> = ({ rotation, isActive, playerStatus, currentStep, onPlay, onPause, onResume, onDelete }) => {
  const playing = isActive && playerStatus === 'playing';
  const paused = isActive && playerStatus === 'paused';

  return (
    <div style={{
      ...styles.rotationCard,
      ...(isActive ? styles.rotationCardActive : {}),
    }}>
      <div style={styles.rotationHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm }}>
          <strong style={{ color: COLORS.textPrimary, fontSize: FONT_SIZE.sm }}>{rotation.name}</strong>
          <Badge tone={isActive ? 'info' : 'neutral'} size="sm">
            {rotation.steps.length} {rotation.steps.length === 1 ? 'step' : 'steps'}
          </Badge>
          {playing && <Badge tone="success" size="sm">PLAYING</Badge>}
          {paused && <Badge tone="warning" size="sm">PAUSED</Badge>}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {!playing && !paused && (
            <Button variant="brass" size="sm" onClick={onPlay} title="Play rotation">
              ▶ Play
            </Button>
          )}
          {playing && (
            <Button variant="secondary" size="sm" onClick={onPause} title="Pause rotation">
              ⏸ Pause
            </Button>
          )}
          {paused && (
            <Button variant="brass" size="sm" onClick={onResume} title="Resume rotation">
              ▶ Resume
            </Button>
          )}
          <button
            type="button"
            onClick={onDelete}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ff6b6b',
              cursor: 'pointer',
              padding: '2px 6px',
              fontSize: 12,
            }}
            title="Delete rotation"
          >
            ✕
          </button>
        </div>
      </div>

      <div style={styles.stepStrip}>
        {rotation.steps.map((s, i) => {
          const isCurrent = isActive && i === currentStep;
          return (
            <span
              key={i}
              style={{
                ...styles.stepPill,
                ...(isCurrent ? styles.stepPillActive : {}),
              }}
            >
              <span>{i + 1}.</span>
              <span>{s.abilityId}</span>
              {s.delayMs > 0 && <span style={{ opacity: 0.6 }}>({s.delayMs}ms)</span>}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export interface RotationMacrosPanelProps {
  rotations: RotationDefinition[];
  activeRotationId: string | null;
  playerStatus: RotationStatus;
  currentStep: number;
  /** Number of steps recorded so far, or null when not recording. */
  recordingStepCount: number | null;
  onStartRecording: () => void;
  onSaveRecording: () => void;
  onCancelRecording: () => void;
  onStop: () => void;
  onPlay: (id: string) => void;
  onPause: () => void;
  onResume: () => void;
  onDelete: (id: string) => void;
}

export const RotationMacrosPanel: React.FC<RotationMacrosPanelProps> = ({
  rotations,
  activeRotationId,
  playerStatus,
  currentStep,
  recordingStepCount,
  onStartRecording,
  onSaveRecording,
  onCancelRecording,
  onStop,
  onPlay,
  onPause,
  onResume,
  onDelete,
}) => {
  const recording = recordingStepCount !== null;

  return (
    <Section title="Rotation Macros">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: SPACE.sm }}>
        {!recording ? (
          <Button variant="danger" size="sm" onClick={onStartRecording}>
            ● Record Macro
          </Button>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, flex: 1 }}>
            <Badge tone="danger" size="md">
              ● RECORDING ({recordingStepCount} steps)
            </Badge>
            <Button variant="brass" size="sm" onClick={onSaveRecording}>
              Save
            </Button>
            <Button variant="ghost" size="sm" onClick={onCancelRecording}>
              Cancel
            </Button>
          </div>
        )}

        <Button
          variant="secondary"
          size="sm"
          onClick={onStop}
          disabled={playerStatus === 'idle'}
        >
          ■ Stop Active
        </Button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: SPACE.xs }}>
        {rotations.length === 0 ? (
          <div style={{ color: COLORS.textMuted, fontStyle: 'italic', fontSize: FONT_SIZE.xs, padding: SPACE.sm, textAlign: 'center' }}>
            No rotations saved yet. Click Record Macro and cast abilities to build a sequence.
          </div>
        ) : (
          rotations.map((r) => (
            <RotationRow
              key={r.id}
              rotation={r}
              isActive={activeRotationId === r.id}
              playerStatus={playerStatus}
              currentStep={currentStep}
              onPlay={() => onPlay(r.id)}
              onPause={onPause}
              onResume={onResume}
              onDelete={() => onDelete(r.id)}
            />
          ))
        )}
      </div>
    </Section>
  );
};
